import React, { useState, useEffect } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Tag } from 'primereact/tag'
import { Message } from 'primereact/message'
import './ContactSearchModal.scss'

// Mock data for demonstration
const mockContacts = [
    {
        id: 1,
        code: 'CON001',
        authorKey: 'SMI-JOH-1965',
        name: 'John Smith',
        contactType: 'Writer',
        dateOfBirth: '1965-03-15',
        names: [
            { id: 1, name: 'John Smith', type: 'Correspondence', ipiNo: null },
            { id: 2, name: 'J. Smith', type: 'MWN', ipiNo: 'I-123456789-0' },
            { id: 3, name: 'Johnny S', type: 'Pseudonym', ipiNo: 'I-123456789-1' }
        ],
        memberships: [
            { id: 1, type: 'Ordinary', typeCode: 'ORD', startDate: '2010-01-01', endDate: null },
            { id: 2, type: 'Successor', typeCode: 'SUC', startDate: '2005-01-01', endDate: '2009-12-31' }
        ],
        works: [
            { id: 1, workType: 'TV', title: 'Programme ABC', episode: 'Episode 1' },
            { id: 2, workType: 'Radio', title: 'Show XYZ', episode: null },
            { id: 3, workType: 'TV', title: 'Documentary Series', episode: 'Part 2' }
        ]
    },
    {
        id: 2,
        code: 'CON002',
        authorKey: 'JON-SAR-1978',
        name: 'Sarah Jones',
        contactType: 'Writer',
        dateOfBirth: '1978-07-22',
        names: [
            { id: 1, name: 'Sarah Jones', type: 'Correspondence', ipiNo: null },
            { id: 2, name: 'S. Jones', type: 'MWN', ipiNo: 'I-987654321-0' }
        ],
        memberships: [
            { id: 1, type: 'Ordinary', typeCode: 'ORD', startDate: '2015-06-01', endDate: null }
        ],
        works: [
            { id: 1, workType: 'TV', title: 'Drama Series', episode: 'Season 2 Ep 5' },
            { id: 2, workType: 'Film', title: 'Feature Film', episode: null }
        ]
    },
    {
        id: 3,
        code: 'CON003',
        authorKey: 'WIL-JAM-1982',
        name: 'James Williams',
        contactType: 'Composer',
        dateOfBirth: '1982-11-30',
        names: [
            { id: 1, name: 'James Williams', type: 'MWN', ipiNo: 'I-555666777-0' },
            { id: 2, name: 'J-Wills', type: 'Pseudonym', ipiNo: 'I-555666777-1' },
            { id: 3, name: 'Jim Williams', type: 'Pseudonym', ipiNo: null }
        ],
        memberships: [
            { id: 1, type: 'Associate', typeCode: 'ASC', startDate: '2020-01-15', endDate: null }
        ],
        works: [
            { id: 1, workType: 'TV', title: 'Soundtrack Album', episode: null },
            { id: 2, workType: 'Radio', title: 'Radio Play Music', episode: 'Episode 3' }
        ]
    },
    {
        id: 4,
        code: 'CON004',
        authorKey: 'BRO-EMM-1990',
        name: 'Emma Brown',
        contactType: 'Writer',
        dateOfBirth: '1990-04-18',
        names: [
            { id: 1, name: 'Emma Brown', type: 'Correspondence', ipiNo: null },
            { id: 2, name: 'E. Brown', type: 'MWN', ipiNo: 'I-111222333-0' },
            { id: 3, name: 'E.B. Writer', type: 'Pseudonym', ipiNo: null }
        ],
        memberships: [
            { id: 1, type: 'Successor', typeCode: 'SUC', startDate: '2022-01-01', endDate: null }
        ],
        works: [
            { id: 1, workType: 'TV', title: 'Comedy Show', episode: 'Pilot' }
        ]
    },
    {
        id: 5,
        code: 'CON005',
        authorKey: 'DAV-MIC-1955',
        name: 'Michael Davis',
        contactType: 'Writer',
        dateOfBirth: '1955-09-05',
        names: [
            { id: 1, name: 'Michael Davis', type: 'MWN', ipiNo: 'I-444555666-0' }
        ],
        memberships: [
            { id: 1, type: 'Ordinary', typeCode: 'ORD', startDate: '1985-03-01', endDate: null },
            { id: 2, type: 'Honorary', typeCode: 'HON', startDate: '2020-01-01', endDate: null }
        ],
        works: [
            { id: 1, workType: 'TV', title: 'Classic Drama', episode: 'Series Finale' },
            { id: 2, workType: 'Radio', title: 'Radio Plays Collection', episode: 'Volume 3' },
            { id: 3, workType: 'Film', title: 'Award Winning Film', episode: null },
            { id: 4, workType: 'TV', title: 'Documentary', episode: 'Part 1' }
        ]
    }
]

const ContactSearchModal = ({ visible, onHide, onConfirm }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [selectedContact, setSelectedContact] = useState(null)
    const [selectedName, setSelectedName] = useState(null)
    const [selectedMembership, setSelectedMembership] = useState(null)

    // Reset state when modal closes
    useEffect(() => {
        if (!visible) {
            setSearchTerm('')
            setSearchResults([])
            setSelectedContact(null)
            setSelectedName(null)
            setSelectedMembership(null)
        }
    }, [visible])

    // Auto-select MWN and first non-successor membership when contact is selected
    useEffect(() => {
        if (selectedContact) {
            // Select MWN by default
            const mwn = selectedContact.names.find(n => n.type === 'MWN')
            setSelectedName(mwn || selectedContact.names[0])

            // Select first non-successor membership
            const nonSuccessor = selectedContact.memberships.find(m => m.type !== 'Successor')
            setSelectedMembership(nonSuccessor || null)
        } else {
            setSelectedName(null)
            setSelectedMembership(null)
        }
    }, [selectedContact])

    const handleSearch = () => {
        if (!searchTerm.trim()) {
            setSearchResults([])
            return
        }
        const term = searchTerm.toLowerCase()
        const results = mockContacts.filter(c =>
            c.name.toLowerCase().includes(term) ||
            c.code.toLowerCase().includes(term) ||
            c.authorKey.toLowerCase().includes(term)
        )
        setSearchResults(results)
        setSelectedContact(null)
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const handleConfirm = () => {
        if (selectedContact && selectedName && selectedMembership) {
            onConfirm({
                contact: selectedContact,
                name: selectedName,
                membership: selectedMembership
            })
        }
    }

    // Check if any selected membership is a successor
    const hasSuccessorSelected = selectedMembership?.type === 'Successor'
    const canConfirm = selectedContact && selectedName && selectedMembership && !hasSuccessorSelected

    // Name row class - grey out Correspondence unless it's the only name
    const nameRowClassName = (data) => {
        if (data.type === 'Correspondence' && selectedContact?.names.length > 1) {
            return 'disabled-row'
        }
        return ''
    }

    // Membership row class - grey out Successor
    const membershipRowClassName = (data) => {
        if (data.type === 'Successor') {
            return 'disabled-row'
        }
        return ''
    }

    // Check if name is selectable
    const isNameSelectable = (name) => {
        if (name.type === 'Correspondence' && selectedContact?.names.length > 1) {
            return false
        }
        return true
    }

    // Check if membership is selectable
    const isMembershipSelectable = (membership) => {
        return membership.type !== 'Successor'
    }

    const footerContent = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text"
            />
            <Button
                label="Confirm Contributor"
                icon="pi pi-check"
                onClick={handleConfirm}
                disabled={!canConfirm}
                className="p-button-primary"
            />
        </div>
    )

    const contactTypeTemplate = (rowData) => {
        return <Tag value={rowData.contactType} severity="info" />
    }

    const nameTypeTemplate = (rowData) => {
        const severity = rowData.type === 'MWN' ? 'success' :
                        rowData.type === 'Pseudonym' ? 'warning' : 'secondary'
        return <Tag value={rowData.type} severity={severity} />
    }

    const membershipTypeTemplate = (rowData) => {
        const severity = rowData.type === 'Successor' ? 'danger' :
                        rowData.type === 'Ordinary' ? 'success' : 'info'
        return <Tag value={rowData.type} severity={severity} />
    }

    return (
        <Dialog
            header="Contact Search"
            visible={visible}
            style={{ width: '95vw', height: '90vh' }}
            onHide={onHide}
            modal
            draggable={false}
            maximizable
            footer={footerContent}
            className="contact-search-modal"
        >
            <div className="modal-content">
                {/* Search Header */}
                <div className="search-header">
                    <div className="p-inputgroup">
                        <InputText
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Search by name, code, or author key..."
                            className="flex-1"
                        />
                        <Button
                            icon="pi pi-search"
                            onClick={handleSearch}
                            className="p-button-primary"
                        />
                    </div>
                </div>

                {/* Panels Container */}
                <div className="panels-container">
                    {/* Search Results Row */}
                    <div className="row g-0 search-results-row">
                        <div className="col-12">
                            <div className="panel search-results-panel" style={{ margin: '20px' }} >
                                <h4 className="panel-title">Search Results</h4>
                                <DataTable
                                    value={searchResults}
                                    selection={selectedContact}
                                    onSelectionChange={(e) => setSelectedContact(e.value)}
                                    selectionMode="single"
                                    dataKey="id"
                                    scrollable
                                    scrollHeight="flex"
                                    emptyMessage="No contacts found. Enter a search term above."
                                    className="panel-table"
                                >
                                    <Column field="code" header="Code" style={{ width: '12%' }} />
                                    <Column field="authorKey" header="Author Key" style={{ width: '18%' }} />
                                    <Column field="name" header="Name" style={{ width: '25%' }} />
                                    <Column field="contactType" header="Type" body={contactTypeTemplate} style={{ width: '12%' }} />
                                    <Column field="dateOfBirth" header="DOB" style={{ width: '12%' }} />
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    {/* Child Panels Row */}
                    <div className="row g-0 child-panels-row">
                        {/* Names Panel */}
                        <div className="col-4">
                            <div className="panel names-panel" style={{ margin: '20px' }} >
                                <h4 className="panel-title">Associated Names</h4>
                                {selectedContact ? (
                                    <DataTable
                                        value={selectedContact.names}
                                        selection={selectedName}
                                        onSelectionChange={(e) => {
                                            if (isNameSelectable(e.value)) {
                                                setSelectedName(e.value)
                                            }
                                        }}
                                        selectionMode="single"
                                        dataKey="id"
                                        scrollable
                                        scrollHeight="flex"
                                        rowClassName={nameRowClassName}
                                        className="panel-table"
                                    >
                                        <Column field="name" header="Name" style={{ width: '50%' }} />
                                        <Column field="type" header="Type" body={nameTypeTemplate} style={{ width: '25%' }} />
                                        <Column field="ipiNo" header="IPI No" style={{ width: '25%' }} />
                                    </DataTable>
                                ) : (
                                    <div className="empty-panel-message">
                                        Select a contact to view names
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Memberships Panel */}
                        <div className="col-4">
                            <div className="panel memberships-panel" style={{ margin: '20px' }}>
                                <h4 className="panel-title">Memberships</h4>
                                {selectedContact ? (
                                    <>
                                        {hasSuccessorSelected && (
                                            <Message
                                                severity="warn"
                                                text="Successor memberships cannot be used for contributors"
                                                className="mb-2 w-full"
                                            />
                                        )}
                                        <DataTable
                                            value={selectedContact.memberships}
                                            selection={selectedMembership}
                                            onSelectionChange={(e) => {
                                                if (isMembershipSelectable(e.value)) {
                                                    setSelectedMembership(e.value)
                                                }
                                            }}
                                            selectionMode="single"
                                            dataKey="id"
                                            scrollable
                                            scrollHeight="flex"
                                            rowClassName={membershipRowClassName}
                                            className="panel-table"
                                        >
                                            <Column field="type" header="Type" body={membershipTypeTemplate} style={{ width: '35%' }} />
                                            <Column field="startDate" header="Start Date" style={{ width: '30%' }} />
                                            <Column field="endDate" header="End Date" style={{ width: '30%' }} />
                                        </DataTable>
                                    </>
                                ) : (
                                    <div className="empty-panel-message">
                                        Select a contact to view memberships
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Works Info Panel */}
                        <div className="col-4">
                            <div className="panel works-panel" style={{ margin: '20px' }}>
                                <h4 className="panel-title">Works Information</h4>
                                {selectedContact ? (
                                    <DataTable
                                        value={selectedContact.works}
                                        dataKey="id"
                                        scrollable
                                        scrollHeight="flex"
                                        className="panel-table"
                                    >
                                        <Column field="workType" header="Work Type" style={{ width: '20%' }} />
                                        <Column field="title" header="Title" style={{ width: '50%' }} />
                                        <Column field="episode" header="Episode" style={{ width: '30%' }} />
                                    </DataTable>
                                ) : (
                                    <div className="empty-panel-message">
                                        Select a contact to view works
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Selection Summary */}
                <div className="row g-0 selection-summary">
                    <div className="col-4">
                        <div className="summary-field" style={{ margin: '20px' }}>
                            <label htmlFor="selectedAuthorKey">Author Key</label>
                            <InputText
                                id="selectedAuthorKey"
                                value={selectedContact?.authorKey || ''}
                                readOnly
                                className="p-inputtext-sm w-100"
                            />
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="summary-field" style={{ margin: '20px' }}>
                            <label htmlFor="selectedName">Name</label>
                            <InputText
                                id="selectedName"
                                value={selectedName?.name || ''}
                                readOnly
                                className="p-inputtext-sm w-100"
                            />
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="summary-field" style={{ margin: '20px' }}>
                            <label htmlFor="selectedMembership">Membership Number</label>
                            <InputText
                                id="selectedMembership"
                                value={selectedMembership?.typeCode || ''}
                                readOnly
                                className="p-inputtext-sm w-100"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default ContactSearchModal
